import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import type { ResourceHttpApiData } from '@/shared/constant/ResourceData';
import { getRequestBaseUrl, getWebsocketRequestBaseUrl } from '@/shared/util/requestBaseUrl';
import { ModelType } from '@tarslib/types/lib/common';
import { EnvironmentMockType } from '@tarslib/types/lib/environment';
import type { SelectDropdownProps } from 'aui';
import { SelectRichDropdown, Tooltip } from 'aui';
import clsx from 'clsx';
import type React from 'react';
import { useMemo, useState } from 'react';
import { VariablesText } from '../variables-text/VariablesText';
import styles from './ApiRequestUrl.module.css';

type ApiRequestUrlProps = {
  showBaseURL?: boolean;
  path: string;
  className?: React.HTMLAttributes<HTMLDivElement>['className'];
  pathClassName?: React.HTMLAttributes<HTMLDivElement>['className'];
  selectProps?: Omit<SelectDropdownProps, 'options' | 'onChange' | 'value'>;
  copyable?: boolean;
  showIconOnly?: boolean;
  selectInline?: boolean;
  whitespace?: 'nowrap' | 'normal';
  showPathOnly?: boolean;
  forceHideBaseUrl?: boolean;
};

export const ApiRequestUrl: React.FC<ApiRequestUrlProps> = (props) => {
  const {
    showBaseURL,
    path,
    className,
    pathClassName,
    selectProps,
    copyable = false,
    showIconOnly = false,
    selectInline = false,
    whitespace = 'nowrap',
    showPathOnly = false,
    forceHideBaseUrl = false,
  } = props;

  const servers = useDocsDataModelStore((x) => x.environments.servers);
  const selectedEnvironment = useDocsDataModelStore((x) => x.environments.selectedEnvironment);
  const selectEnvironment = useDocsDataModelStore((x) => x.selectEnvironment);
  const environmentList = useDocsDataModelStore((x) => x.environments.environmentList);
  const serverId = useDocsDataModelStore(
    (x) => (x.resourceData.extraData as ResourceHttpApiData['extraData']).serverId,
  );
  const resourceType = useDocsDataModelStore((x) => x.resourceData.type);
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);

  const [selectDropdownOpen, setSelectDropdownOpen] = useState(false);

  const options = useMemo(() => {
    return environmentList.reduce<
      {
        label: React.ReactNode;
        value: number;
      }[]
    >((acc, env) => {
      if (
        resourceType === ModelType.WebSocketApi &&
        env.type &&
        EnvironmentMockType.includes(env.type)
      ) {
        return acc;
      }
      const baseUrl =
        resourceType === ModelType.WebSocketApi
          ? getWebsocketRequestBaseUrl({
              serverId,
              environment: env,
            })
          : getRequestBaseUrl({
              serverId,
              environment: env,
              servers,
            });
      const option = {
        label: (
          <VariablesText
            text={baseUrl || ''}
            className={styles['select-label']}
            onTagClick={(e) => {
              setSelectDropdownOpen(false);
            }}
          />
        ),
        value: env.id,
        extra: (
          <p className={styles['select-extra']} style={{ color: 'var(--ui-disabled-color)' }}>
            {env.name}
          </p>
        ),
      };

      acc.push(option);
      return acc;
    }, []);
  }, [environmentList, serverId, servers, resourceType]);

  const selectedEnvironmentBaseUrl = useMemo(() => {
    return resourceType === ModelType.WebSocketApi
      ? getWebsocketRequestBaseUrl({
          serverId,
          environment: selectedEnvironment,
        })
      : getRequestBaseUrl({
          serverId,
          environment: selectedEnvironment,
          servers,
        });
  }, [selectedEnvironment, serverId, servers, resourceType]);

  const showBaseUrlOnly = useMemo(() => options.length <= 1, [options]);

  const handleServerChange = (value: any) => {
    selectEnvironment(value);
  };

  let baseUrlContent = null;

  const BaseUrlIcon = (
    <Tooltip
      title={
        <div>
          {clientI18n('baseURL.colon')}
          <VariablesText
            text={selectedEnvironmentBaseUrl}
            copyable={copyable}
            copyableText={selectedEnvironmentBaseUrl}
          />
        </div>
      }
      placement="top"
    >
      <span
        className={clsx(
          'flex shrink-0 cursor-pointer items-center justify-center rounded border-[0.5px] border-border-color-base px-[6px] py-1',
        )}
      >
        {selectProps?.optionLabelIcon}
      </span>
    </Tooltip>
  );

  if (showPathOnly) {
    return (
      <div className={clsx(className)}>
        <VariablesText
          className={clsx('ml-2', pathClassName)}
          text={path}
          copyable={copyable}
          copyableText={`${selectedEnvironmentBaseUrl}${path}`}
        />
      </div>
    );
  }

  if (forceHideBaseUrl) {
    return (
      <div className={clsx(className, 'flex items-center')}>
        <VariablesText
          className={clsx('ml-2', pathClassName)}
          text={path}
          copyable={copyable}
          copyableText={path}
        />
      </div>
    );
  }

  if (!showBaseURL) {
    let content: React.ReactNode = BaseUrlIcon;
    if (!selectedEnvironment) {
      content = null;
    }
    return (
      <div className={clsx(className, 'flex items-center')}>
        {content}
        <VariablesText
          className={clsx('ml-2', pathClassName)}
          text={path}
          copyable={copyable}
          copyableText={`${selectedEnvironmentBaseUrl}${path}`}
        />
      </div>
    );
  }

  if (showIconOnly && selectProps) {
    return (
      <div className={clsx(className)}>
        <div className="flex items-center">
          {BaseUrlIcon}
          <VariablesText
            className={clsx('ml-2', pathClassName)}
            text={path}
            copyable={copyable}
            copyableText={`${selectedEnvironmentBaseUrl}${path}`}
          />
        </div>
      </div>
    );
  }

  if (showBaseURL && selectedEnvironment && showBaseUrlOnly) {
    if (selectProps?.optionLabelIconOnly) {
      baseUrlContent = BaseUrlIcon;
    } else {
      baseUrlContent = (
        <VariablesText
          className={clsx(
            'inline-block cursor-default rounded border-[0.5px] border-border-color-base px-2 leading-[23px]',
          )}
          text={selectedEnvironmentBaseUrl}
          copyable={copyable}
          copyableText={`${selectedEnvironmentBaseUrl}${path}`}
        />
      );

      return (
        <div className={clsx(className)}>
          <div style={{ whiteSpace: whitespace }}>
            {baseUrlContent}
            <VariablesText
              className={clsx('ml-2', pathClassName)}
              text={path}
              copyable={copyable}
              copyableText={`${selectedEnvironmentBaseUrl}${path}`}
            />
          </div>
        </div>
      );
    }
  }

  if (showBaseURL && selectedEnvironment && !showBaseUrlOnly) {
    baseUrlContent = (
      <SelectRichDropdown
        {...selectProps}
        className={selectInline ? styles['api-request-url'] : ''}
        value={selectedEnvironment?.id}
        options={options}
        popupClassName={styles.popup}
        onChange={handleServerChange}
        size="small"
        open={selectDropdownOpen}
        onDropdownVisibleChange={setSelectDropdownOpen}
      />
    );

    if (selectProps?.optionLabelIconOnly) {
      baseUrlContent = (
        <Tooltip
          title={`${clientI18n('baseURL.colon')}${selectedEnvironmentBaseUrl}`}
          autoAdjustOverflow={false}
          placement="left"
          open={options.length > 1 ? false : undefined}
        >
          <SelectRichDropdown
            {...selectProps}
            optionLabelIcon={
              <div className="flex h-full w-full items-center justify-center">
                {selectProps.optionLabelIcon}
              </div>
            }
            value={selectedEnvironment?.id}
            options={options}
            popupClassName={styles.popup}
            onChange={handleServerChange}
            size="small"
            open={selectDropdownOpen}
            onDropdownVisibleChange={setSelectDropdownOpen}
          />
        </Tooltip>
      );
    }
  }

  return (
    <div className={clsx(className)}>
      {selectedEnvironmentBaseUrl && baseUrlContent}
      <VariablesText
        className={clsx('ml-2', pathClassName)}
        text={path}
        copyable={copyable}
        copyableText={`${selectedEnvironmentBaseUrl}${path}`}
      />
    </div>
  );
};
